/**
 * Launcher — dlaždice spouštěče a jejich úprava.
 * Jeden vzhled pro start menu i Aplikace: mřížka dlaždic (ikona + název),
 * v režimu úprav seznam se zaškrtnutím a šipkami pro pořadí.
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface LauncherGridProps {
  children: ReactNode;
  /** Kompaktní = vždy dva sloupce jako na telefonu. */
  compact?: boolean;
  className?: string;
}

/** Mřížka dlaždic. Telefon dva sloupce, desktop hustší mřížka. */
export function LauncherGrid({ children, compact = false, className }: LauncherGridProps) {
  return (
    <div className={cn('phone-tiles', !compact && 'start-menu-tiles', className)}>
      {children}
    </div>
  );
}

export interface LauncherTileProps {
  to: string;
  icon: LucideIcon;
  label: string;
  /** Přes dva sloupce. */
  wide?: boolean;
  /** NavLink `end`: aktivní jen na přesné cestě. */
  end?: boolean;
}

export function LauncherTile({ to, icon: Icon, label, wide, end }: LauncherTileProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn('phone-tile', wide && 'phone-tile-wide', isActive && 'phone-tile-active')}
    >
      <Icon size={28} strokeWidth={1.5} className='phone-tile-icon' />
      <span className='phone-tile-label'>{label}</span>
    </NavLink>
  );
}

export interface LauncherEditRow {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Zaškrtnuto = dlaždice se ukazuje. */
  on: boolean;
  /** Pořadí, když ho řádek sám neukazuje polohou (např. „#2"). */
  badge?: string;
  /** Šipky pro pořadí. Výchozí: jen u zaškrtnutých. */
  movable?: boolean;
}

export interface LauncherEditListProps {
  rows: LauncherEditRow[];
  onToggle: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
}

/** Úprava spouštěče: zaškrtnutí ukáže / skryje, šipky mění pořadí. */
export function LauncherEditList({ rows, onToggle, onMove }: LauncherEditListProps) {
  const { t } = useTranslation();
  return (
    <ul className='space-y-1.5'>
      {rows.map((row) => {
        const Icon = row.icon;
        const movable = row.movable ?? row.on;
        return (
          <li
            key={row.id}
            className={cn(
              'flex items-center gap-2 rounded-xl border px-3 py-2',
              row.on ? 'border-plex/40 bg-plex/10' : 'border-gray-800 bg-gray-950/40',
            )}
          >
            <button
              type='button'
              role='checkbox'
              aria-checked={row.on}
              className='flex items-center gap-2 min-w-0 flex-1 text-left'
              onClick={() => onToggle(row.id)}
            >
              <span
                className={cn(
                  'w-5 h-5 rounded border flex items-center justify-center text-[10px]',
                  row.on ? 'border-plex bg-plex/30 text-white' : 'border-gray-700 text-transparent',
                )}
              >
                {row.on ? '✓' : '·'}
              </span>
              <Icon size={16} className='text-plex shrink-0' />
              <span
                className={cn('text-sm truncate', row.on ? 'text-gray-100' : 'text-gray-500')}
              >
                {row.label}
              </span>
              {row.badge && (
                <span className='text-[10px] text-gray-500 tabular-nums ml-auto'>
                  {row.badge}
                </span>
              )}
            </button>
            {movable && (
              <div className='flex gap-0.5 shrink-0'>
                <button
                  type='button'
                  className='px-1.5 py-0.5 text-xs text-gray-500 hover:text-white'
                  onClick={() => onMove(row.id, -1)}
                  aria-label={t('ui.launcher.moveUp', 'Posunout výš')}
                  title={t('ui.launcher.moveUp', 'Posunout výš')}
                >
                  ↑
                </button>
                <button
                  type='button'
                  className='px-1.5 py-0.5 text-xs text-gray-500 hover:text-white'
                  onClick={() => onMove(row.id, 1)}
                  aria-label={t('ui.launcher.moveDown', 'Posunout níž')}
                  title={t('ui.launcher.moveDown', 'Posunout níž')}
                >
                  ↓
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
